import type {ComponentPropsWithoutRef, ElementType, ReactElement} from 'react';

type WhiteBgButtonOwnProps<E extends ElementType = 'button'> = {
  as?: E;
  label: string;
  isPrimary?: boolean;
  isDisabled?: boolean;
  variant?: 'regular' | 'thin';
  isLarge?: boolean;
};

type WhiteBgButtonProps<E extends ElementType = 'button'> =
  WhiteBgButtonOwnProps<E> &
    Omit<ComponentPropsWithoutRef<E>, keyof WhiteBgButtonOwnProps<E>>;

const getColorClass = (
  isPrimary: boolean,
  isDisabled: boolean,
): {bgClass: string; textColorClass: string} => {
  if (isDisabled) {
    return {
      bgClass: 'bg-disabled-1 pointer-events-none',
      textColorClass: 'text-black-4',
    };
  }

  return isPrimary
    ? {
        bgClass:
          'bg-white border-accent-1 pc:hover:border-accent-2 pc:hover:ease-in-out pc:hover:duration-300',
        textColorClass:
          'text-accent-1 pc:group-hover:text-accent-2 pc:hover:ease-in-out pc:hover:duration-300',
      }
    : {
        bgClass:
          'bg-white border-primary-1 pc:hover:border-primary-2 pc:hover:ease-in-out pc:hover:duration-300',
        textColorClass:
          'text-primary-1 pc:group-hover:text-primary-2 pc:hover:ease-in-out pc:hover:duration-300',
      };
};

export const WhiteBgButton = <E extends ElementType = 'button'>(
  props: WhiteBgButtonProps<E>,
): ReactElement => {
  const {
    as,
    label,
    isPrimary = true,
    isLarge = true,
    isDisabled = false,
    className = '',
    variant = 'regular',
    ...rest
  } = props;

  const Component = as ?? 'button';
  const {bgClass, textColorClass} = getColorClass(isPrimary, isDisabled);

  const isThin = variant === 'thin';
  const textSizeClass = isThin ? 'text-sm' : isLarge ? 'text-lg' : 'text';
  const paddingClass = isThin ? 'py-[7px]' : isLarge ? 'py-[9px]' : 'py-[10px]';
  const fontWeightClass = isThin ? 'font-normal' : 'font-bold';

  const combinedClassName = [
    bgClass,
    paddingClass,
    'rounded-4',
    'shadow-bottom',
    'group',
    'flex',
    'min-w-[100px]',
    'items-center',
    'justify-between',
    'border',
    'border-solid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const spanClassName = [
    textSizeClass,
    textColorClass,
    'leading',
    'block',
    'w-full',
    'text-center',
    fontWeightClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component
      className={combinedClassName}
      {...(Component === 'button' && {type: 'button'})}
      {...rest}
    >
      <span className={spanClassName}>{label}</span>
    </Component>
  );
};

// 互換性のためのエイリアス
export const WhiteBgButtonThin = <E extends ElementType = 'button'>(
  props: Omit<WhiteBgButtonProps<E>, 'variant' | 'isLarge'>,
): ReactElement => {
  return (
    <WhiteBgButton<E> {...(props as WhiteBgButtonProps<E>)} variant="thin" />
  );
};
